import {
  ESuperSiteTargetingLogicOperator,
  ESuperSiteTargetingObjectEndpointParamsType,
  ESuperSiteTargetingObjectType,
} from '~/src/dash/api/channels/channels_models';
import { ESuperSiteTargetingType, ResourceActive } from '~/src/dash/api/console-api';
import { IExchangeRate } from '~/src/dash/api/exchange-rates';
import { encodeForm } from '~/src/dash/api/resource';
import { createUrl, restDelete, restGet, restPatch, restPost } from '~/src/dash/api/rest';
import { EBool } from '~/src/dash/api/types/bool';
import { RowsPerPage } from '~/src/dash/components/tables/ListViewTable/common';
import { EDeviceTypes } from '~/src/dash/utils/devices';

export type orderDirection = 'asc' | 'desc';
export type activeFilter = ResourceActive;

// functions and interfaces for Channels
interface IChannelLabel {
  ID: string;
  Name: string;
  Sites: IChannel[];
  CreatedAt: string;
}

export interface IChannel {
  Active: activeFilter;
  BidFloor: number;
  ChannelLabels: IChannelLabel[];
  ExchangeRate: IExchangeRate;
  ID: string;
  Name: string;
  SitesCronSummary: {
    Last24HrAverageCPM: number;
    Last24HrEndpointRequests: number;
    Last24HrImpressions: number;
    Last24HrRevenue: number;
    PastWeekAverageCPM: number;
    PastWeekEndpointRequests: number;
    PastWeekImpressions: number;
    PastWeekImpressionsTrend: number;
    PastWeekRevenue: number;
    PastWeekRevenueTrend: number;
  };
  TotalCount: number;
  UUID: string;
}

export interface IChannelRow {
  Active: activeFilter;
  BidFloor: number;
  ChannelLabels: string[];
  ExchangeRate: IExchangeRate;
  DefaultWeight?: number;
  ID: string;
  Last24HrAverageCPM: number;
  Last24HrEndpointRequests: number;
  Last24HrImpressions: number;
  Last24HrRevenue: number;
  PastWeekAverageCPM: number;
  PastWeekEndpointRequests: number;
  PastWeekImpressions: number;
  PastWeekImpressionsTrend: number;
  PastWeekRevenue: number;
  PastWeekRevenueTrend: number;
  Name: string;
  UUID: string;
}

export interface IChannels {
  Channels: IChannel[];
  TotalRows: number;
}

export interface IChannelsUiParams {
  orderBy?: string;
  order?: orderDirection;
  searchInput?: string | '';
  currPage?: number;
  rowsPerPage?: RowsPerPage;
  activeColumnKeys?: string[];
  isTableExpanded?: boolean;
}

export interface IGetChannelsAPIParams {
  orderBy?: string;
  orderDirection?: orderDirection;
  nameSearch?: string;
  idSearch?: number | string;
  activeFilter?: activeFilter;
  page?: number;
  pageSize?: RowsPerPage;
  siteIDs?: number[];
  preloads?: string[];
  uiFields?: string[];
}

export interface IGetChannelsResponse {
  Channels: IChannel[];
  TotalRows: number;
}

export interface INumberBoolMap {
  [key: number]: boolean;
}

// UI key -> API key
const channnelAPIKeyMap: { [key: string]: string } = {
  publisherID: 'publisher_id',
  channelID: 'site_id',
  siteIDs: 'site_ids',
  orderBy: 'order_by',
  orderDirection: 'order_direction',
  nameSearch: 'name_search',
  idSearch: 'id_search',
  activeFilter: 'active_filter',
  page: 'page',
  pageSize: 'page_size',
  preloads: 'preloads',
  uiFields: 'ui_fields',
};

export async function fetchChannels(
  publisherID: string,
  params: IGetChannelsAPIParams,
): Promise<IGetChannelsResponse> {
  const query = Object.entries(params).map(
    ([key, val]) =>
      `&${channnelAPIKeyMap[key]}=${Array.isArray(val) ? val.join(',') : String(val)}`,
  );
  const reqUrl = `${createUrl('/sites', publisherID, 'v2')}${query.join('')}`;

  try {
    const response = await restGet<any>(reqUrl);
    const { parsedBody } = response;
    return {
      Channels: parsedBody?.sites,
      TotalRows: parsedBody?.totalCount,
    } as IGetChannelsResponse;
  } catch (err) {
    throw err;
  }
}

export async function deleteChannel(publisherID: string, channelID: string): Promise<any> {
  const query = `&${channnelAPIKeyMap.channelID}=${channelID}`;
  const reqUrl = `${createUrl('/site', publisherID, 'v1')}${query}`;

  try {
    const response = await restDelete<any>(reqUrl, {});
    return response;
  } catch (err) {
    throw err;
  }
}

export async function updateChannel(
  publisherID: string,
  channelID: string,
  updates: Partial<IChannelRow>,
): Promise<any> {
  const query = `&${channnelAPIKeyMap.channelID}=${channelID}`;
  const reqUrl = `${createUrl('/update_site', publisherID, 'v1')}${query}`;
  const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
  const body = encodeForm({ site: updates }, headers['Content-Type']);

  try {
    const response = await restPost<any>(reqUrl, updates, { method: 'post', body, headers });
    return response;
  } catch (err) {
    throw err;
  }
}

// functions and interfaces for Channel Groups
export interface IChannelGroupsUiParams {
  channelGroupIDs?: number[];
  orderBy?: string;
  order?: orderDirection;
  searchInput?: string | '';
  currPage?: number;
  rowsPerPage?: RowsPerPage;
  activeColumnKeys?: string[];
  isTableExpanded?: boolean;
}

export interface IGetChannelGroupsAPIParams {
  channelGroupIDs?: number[];
  orderBy?: string;
  orderDirection?: orderDirection;
  nameSearch?: string;
  idSearch?: number | string;
  page?: number | string;
  pageSize?: number | string;
  preloads?: string[];
  uiFields?: string[];
}

export interface ISitesInfo {
  ID: number;
  Name: string;
  BidFloor: number;
}

export interface IChannelGroup {
  id: number;
  AdserverCampaigns: any[];
  BrandSafetyTracker: any[];
  ChannelGroupLabels: any[];
  ChannelIDs: string;
  ChannelLists: any[];
  Deals: any[];
  DefaultWeight: number;
  HeaderBidders: any[];
  Name: string;
  PublisherID: number;
  Sites: any[];
  SitesInfo: ISitesInfo[];
}

export interface IChannelGroupLabel {
  ID: number;
  Name: string;
}

export interface IChannelGroupRow {
  ID: string;
  Name: string;
  PublisherID: number;
  ChannelGroupLabels: IChannelGroupLabel[];
  ChannelIDs: string[] | string;
  DefaultWeight: number;
}

export interface IGetChannelGroupsResponse {
  ChannelGroups: IChannelGroup[];
  TotalRows: number;
  Labels: IChannelGroupLabel[];
}

// UI key -> API key
const ChannelGroupAPIKeyMap: { [key: string]: string } = {
  publisherID: 'publisher_id',
  channelGroupIDs: 'channel_group_ids',
  channelGroupID: 'channel_group_id',
  orderBy: 'order_by',
  orderDirection: 'order_direction',
  nameSearch: 'name_search',
  idSearch: 'id_search',
  page: 'page',
  pageSize: 'page_size',
  preloads: 'preloads',
  uiFields: 'ui_fields',
};

export interface IChannelABTest {
  ID: number;
  CreatedAt?: string;
  UpdatedAt?: string;
  DeletedAt?: string;
  SiteID: number;
  Name: string;
  Active: number;
  StartTime: string;
  EndTime: string;
  Timezone: string;
  TrafficShareTestA: number;
  TrafficShareTestB: number;
  UseSyntheticIDTestA: EBool;
  UseSyntheticIDTestB: EBool;
  UseTruncatedIPTestA: EBool;
  UseTruncatedIPTestB: EBool;
}

export interface IGetChannelABTestsResponse {
  ChannelABTests: IChannelABTest[];
}

export async function fetchChannelABTests(
  publisherID: string,
  channelID: string,
): Promise<IGetChannelABTestsResponse> {
  const reqUrl = `${createUrl('/channel_ab_tests', publisherID, 'v1')}&site_id=${channelID}`;
  try {
    const response = await restGet<any>(reqUrl);
    const { parsedBody } = response;
    return {
      ChannelABTests: parsedBody?.ChannelABTests,
    } as IGetChannelABTestsResponse;
  } catch (err) {
    throw err;
  }
}

export async function createChannelABTest(
  publisherID: string,
  test: IChannelABTest,
): Promise<IChannelABTest> {
  const reqUrl = `${createUrl(`/channel_ab_test`, publisherID, 'v1')}`;
  try {
    const response = await restPost<any>(reqUrl, test);
    const { parsedBody } = response;
    return parsedBody?.ChannelABTest;
  } catch (err) {
    throw err;
  }
}

export async function updateChannelABTest(
  publisherID: string,
  test: IChannelABTest,
): Promise<IChannelABTest> {
  const reqUrl = `${createUrl(`/channel_ab_test/${test.ID}`, publisherID, 'v1')}`;
  try {
    const response = await restPatch<any>(reqUrl, test);
    const { parsedBody } = response;
    return parsedBody?.ChannelABTest;
  } catch (err) {
    throw err;
  }
}

export async function fetchChannelGroups(
  publisherID: string,
  params: IGetChannelGroupsAPIParams,
): Promise<IGetChannelGroupsResponse> {
  const query = Object.entries(params).map(
    ([key, val]) =>
      `&${ChannelGroupAPIKeyMap[key]}=${Array.isArray(val) ? val.join(',') : String(val)}`,
  );
  const reqUrl = `${createUrl('/channel_groups', publisherID, 'v1')}${query.join('')}`;

  try {
    const response = await restGet<any>(reqUrl);
    const { parsedBody } = response;
    return {
      ChannelGroups: parsedBody?.ChannelGroups,
      TotalRows: parsedBody?.TotalCount,
      Labels: parsedBody?.Labels,
    } as IGetChannelGroupsResponse;
  } catch (err) {
    throw err;
  }
}

export async function deleteChannelGroup(
  publisherID: string,
  channelGroupID: string,
): Promise<any> {
  const query = `&${ChannelGroupAPIKeyMap.channelGroupID}=${channelGroupID}`;
  const reqUrl = `${createUrl('/channel_group', publisherID, 'v1')}${query}`;
  try {
    const response = await restDelete<any>(reqUrl, {});
    return response;
  } catch (err) {
    throw err;
  }
}

export async function updateChannelGroup(
  publisherID: string,
  updates: Partial<IChannelGroupRow>,
): Promise<any> {
  const reqUrl = `${createUrl('/channel_group', publisherID, 'v1')}`;
  try {
    const response = await restPatch<any>(reqUrl, updates);
    return response;
  } catch (err) {
    throw err;
  }
}

export async function createChannelGroup(
  publisherID: string,
  data: Partial<IChannelGroupRow>,
): Promise<any> {
  const reqUrl = `${createUrl('/channel_group', publisherID, 'v1')}`;
  try {
    const response = await restPost<any>(reqUrl, data);
    return response;
  } catch (err) {
    throw err;
  }
}

// functions and interfaces for Channel Mappings
interface ISuperSiteTargetingConfig {
  SuperSiteTargetingObjectType: ESuperSiteTargetingObjectType;
  CustomConfigOnlyString: string;
  CustomConfigEndpointParam: {
    ParamType: ESuperSiteTargetingObjectEndpointParamsType;
    Value: string;
  };
  CustomConfigDeviceEnvironment: number[]; // see src/dash/utils/device-environments.ts:8
  CustomConfigDeviceType: EDeviceTypes[];
}

interface ISubSite {
  ID: string;
  Name: string;
  SuperSiteTargeting: string;
  SuperSiteTargetingType: ESuperSiteTargetingType;
  SuperSiteTargetingConfig: ISuperSiteTargetingConfig[];
  SuperSiteTargetingLogicOperator: ESuperSiteTargetingLogicOperator;
}

export interface IGetChannelMappingsUiParams {
  siteIDs?: string[];
  orderBy?: string;
  order?: orderDirection;
  searchInput?: string | '';
  currPage?: number;
  rowsPerPage?: RowsPerPage;
  activeColumnKeys?: string[];
  isTableExpanded?: boolean;
}

export interface IGetChannelMappingsAPIParams {
  siteIDs?: string[];
  orderBy?: string;
  orderDirection?: orderDirection;
  nameSearch?: string;
  idSearch?: number | string;
  page?: number;
  pageSize?: RowsPerPage;
}

export interface IChannelMapping {
  ID: string;
  Name: string;
  SubSites: ISubSite[];
}

export interface IGetChannelMappingsResponse {
  ChannelMappings: IChannelMapping[];
  TotalRows: number;
}

export interface IChannelMappingRow {
  ID: string;
  Name: string;
  SubChannels: ISubSite[];
}

// UI key -> API key
const SuperChannelsAPIKeyMap: { [key: string]: string } = {
  publisherID: 'publisher_id',
  siteIDs: 'site_ids',
  orderBy: 'order_by',
  orderDirection: 'order_direction',
  nameSearch: 'name_search',
  idSearch: 'id_search',
  page: 'page',
  pageSize: 'page_size',
};

export async function fetchChannelMappings(
  publisherID: string,
  params: IGetChannelMappingsAPIParams = {},
): Promise<IGetChannelMappingsResponse> {
  const query = Object.entries(params).map(
    ([key, val]) =>
      `&${SuperChannelsAPIKeyMap[key]}=${Array.isArray(val) ? val.join(',') : String(val)}`,
  );
  const reqUrl = `${createUrl('/super_sites', publisherID, 'v1')}${query.join('')}`;

  try {
    const response = await restGet<any>(reqUrl);
    const { parsedBody } = response;
    return {
      ChannelMappings: parsedBody?.SuperChannels,
      TotalRows: parsedBody?.TotalCount,
    } as IGetChannelMappingsResponse;
  } catch (err) {
    throw err;
  }
}
